import * as React from 'react'
import { Button } from '~/client/components/ui/button'
import { Credenza, CredenzaContent, CredenzaHeader, CredenzaTitle, CredenzaBody, CredenzaFooter } from '~/client/components/ui/credenza'
import { BasilAddSolidIcon } from '~/client/components/icons'
import { useNavigate } from '@tanstack/react-router'

type EventOption = {
  id: string
  title: string
  dateLabel?: string
  capacityLabel?: string
  slug: string
}

type MyEventsCredenzaProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  events: EventOption[]
  isLoading?: boolean
  onCreateEvent?: () => void
}

export function MyEventsCredenza({ open = false, onOpenChange, events, isLoading, onCreateEvent }: MyEventsCredenzaProps) {
  const navigate = useNavigate()

  return (
    <Credenza open={open} onOpenChange={onOpenChange}>
      <CredenzaContent className="sm:rounded-[8px] max-w-full md:max-w-[580px] max-h-[90dvh] min-h-0 flex flex-col">
        <CredenzaHeader>
          <CredenzaTitle className="text-[20px] font-semibold text-[#18003d]">Events</CredenzaTitle>
        </CredenzaHeader>
        <CredenzaBody className="p-[20px] flex-1 min-h-0 overflow-y-auto">
          <div className="rounded-[12px] border border-neutral-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] overflow-hidden bg-white max-h-[400px] overflow-y-auto">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <React.Fragment key={`skeleton-${idx}`}>
                  <div className="px-4 py-[18px]">
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    <div className="mt-2 h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
                  </div>
                  {idx < 3 ? <div className="h-px bg-[#e4e1e9]" /> : null}
                </React.Fragment>
              ))
            ) : events.length > 0 ? (
              events.map((ev, idx) => (
                <React.Fragment key={ev.id}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate({ to: '/events/$eventSlug', params: { eventSlug: ev.slug || ev.id }, search: { tab: 'shortlist' } } as any)
                      onOpenChange?.(false)
                    }}
                    className="w-full text-left px-4 py-[18px] flex items-center justify-between bg-white hover:bg-[rgba(138,61,255,0.06)]"
                  >
                    <div className="flex-1">
                      <div className="text-[14px] font-medium text-[#55534e] leading-[14px]">{ev.title}</div>
                      {(ev.dateLabel || ev.capacityLabel) && (
                        <div className="mt-1 flex gap-1 text-[14px] font-medium text-[#9b9b9a]">
                          {ev.dateLabel ? <span>{ev.dateLabel}</span> : null}
                          {ev.dateLabel && ev.capacityLabel ? <span>•</span> : null}
                          {ev.capacityLabel ? <span>{ev.capacityLabel}</span> : null}
                        </div>
                      )}
                    </div>
                  </button>
                  {idx < events.length - 1 ? <div className="h-px bg-[#e4e1e9]" /> : null}
                </React.Fragment>
              ))
            ) : (
              <div className="px-4 py-[18px] text-sm text-[#9b9b9a]">No events yet</div>
            )}
          </div>
        </CredenzaBody>
        <CredenzaFooter className="p-[20px] pt-3">
          <Button
            variant="revel"
            className="w-full h-11 rounded-[12px]"
            onClick={() => {
              onOpenChange?.(false)
              onCreateEvent?.()
            }}
          >
            Create Event
            <BasilAddSolidIcon variant="white" className="size-6" />
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  )
}
